import { Box, Typography, Paper, styled } from '@mui/material'
import { ArrowDownRight } from 'assets/icons/arrow-down-right'
import { ArrowUpRight } from 'assets/icons/arrow-up-right'
import type { ReactNode } from 'react'

const PercentBox = styled(Typography)(({ theme }) => ({
  color: 'grey.700',
  display: 'flex',
  ml: 'auto',
  alignItems: 'center',
  gap: 0.5,
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  marginLeft: 'auto',
  borderRadius: 6,
  padding: '2px 8px',
}))

export type GridBlockProps = {
  title?: ReactNode
  value: ReactNode
  desc: ReactNode
  color?: string
  icon?: ReactNode
  disablePaddingBottom?: boolean
  preTitleBorder?: boolean
  preTitle?: ReactNode
  percent?: number
}

export const GridBlock = ({
  desc,
  icon,
  title,
  value,
  color,
  disablePaddingBottom = false,
  preTitle,
  preTitleBorder = false,
  percent,
}: GridBlockProps) => {
  return (
    <Box height="100%" display="flex" flexDirection="column">
      {!!preTitle && (
        <Typography
          border={preTitleBorder ? '1px solid' : 'none'}
          borderColor="grey.200"
          sx={{ backgroundColor: 'grey.50', borderTopLeftRadius: 16, borderTopRightRadius: 16 }}
          px={2.5}
          pt={1}
          fontWeight={600}
          pb={2}
          position="relative"
          mb="-12px"
        >
          {preTitle || <>&nbsp;</>}
        </Typography>
      )}
      <Paper
        sx={{
          display: 'flex',
          pt: 2.25,
          px: 2,
          gap: 2,
          pb: disablePaddingBottom ? 0 : 2.25,
          alignItems: 'flex-start',
          flex: 1,
          position: 'relative',
          zIndeX: 2,
        }}
      >
        {!!icon && (
          <Box
            flexBasis={48}
            flexShrink={0}
            flexGrow={0}
            width="48px"
            height="48px"
            sx={{
              boxShadow: `inset 0 0 90px ${color}`,
              border: `8px solid ${color}45;`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        )}
        <Box width="100%">
          {title && (
            <Typography lineHeight="48px" sx={{ verticalAlign: 'middle', mb: 1 }} fontWeight={600}>
              {title}
            </Typography>
          )}
          <Typography fontWeight={600} variant="h4" display={!!percent ? 'flex' : 'block'} alignItems="center">
            {value}
            {!!percent && (
              <PercentBox variant="body2">
                {percent > 0 ? <ArrowUpRight /> : <ArrowDownRight />} {Math.abs(percent)}%<sub>*</sub>
              </PercentBox>
            )}
          </Typography>
          {desc && (
            <Typography mt={1} variant="body2" color="grey.600">
              {desc}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  )
}
